import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import range from 'lodash/range';
import round from 'lodash/round';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';

import types from '../../../../../types';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import BlankLink from '../../../../common/BlankLink';

const fuelOptions = t => [
  { value: 0, display: t('natural gas') },
  { value: 1, display: t('electricity') },
  { value: 2, display: t('fuel oil') },
];

const fuelUnits = (inputTakeactionPurchaseHighEfficiencyHeatingFueltype, isMetric, t) => {
  switch (inputTakeactionPurchaseHighEfficiencyHeatingFueltype) {
    case 0: {
      return `${isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' })}<sup>3</sup>`;
    }
    case 1: {
      return t('kWh', { ns: 'units' });
    }
    case 2: {
      return isMetric ? t('liters', { ns: 'units' }) : t('gallons', { ns: 'units' });
    }
    default: {
      throw new Error(`Unexpected winter fuel type ${inputTakeactionPurchaseHighEfficiencyHeatingFueltype}.`);
    }
  }
};

const percentOptions = range(0.2, 2.2, 0.2).concat([2.5, 3]).map(value => ({
  value: round(value, 1),
  display: `${round(value * 100)} %`,
}));

const EfficientHeatingEquipment = ({
  t,
  locale,
  isMetric,
  widgetSource,
  inputFootprintHousingSquarefeet,
  inputTakeactionPurchaseHighEfficiencyHeatingFueltype,
  resultTakeactionPurchaseHighEfficiencyHeatingDollarstypical,
  resultTakeactionPurchaseHighEfficiencyHeatingDollarsuse,
  resultTakeactionPurchaseHighEfficiencyHeatingIntensity,
  resultTakeactionPurchaseHighEfficiencyHeatingGCO2fueltype,
  resultTakeactionPurchaseHighEfficiencyHeatingPu,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I heat my home with')}
        <InlineSelect
          options={fuelOptions(t)}
          apiKey={'input_takeaction_purchase_high_efficiency_heating_fueltype'}
        />.&nbsp;
        <span
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line no-template-curly-in-string
            __html: t('Typical {{areaValue}} {{areaUnit}}<sup>2</sup> homes in my climate spend ${{dollarsTypical}} per year on heating. I heat my home about', {
              areaValue: inputFootprintHousingSquarefeet.ensuredDisplay,
              areaUnit: isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' }),
              dollarsTypical: resultTakeactionPurchaseHighEfficiencyHeatingDollarstypical,
            }),
          }}
        />
        <InlineSelect
          options={percentOptions}
          apiKey={'input_takeaction_purchase_high_efficiency_heating_percentuse'}
        />
        {
          // eslint-disable-next-line no-template-curly-in-string
          t('of typical households, or ${{resultTakeactionPurchaseHighEfficiencyHeatingDollarsuse}} per year.', {
            resultTakeactionPurchaseHighEfficiencyHeatingDollarsuse,
          })
        }
      </p>
    </div>
    <div className="action-details__links">
      {widgetSource !== 'ontario' && <a
        href="http://www.coolcalifornia.org/funding-wizard#search/heating//false/none/false/1/12"
        target="_blank"
        rel="noopener noreferrer"
      >{t('Find rebates and incentives for this action')}</a>}
      {widgetSource === 'ontario' &&
        <div>
          <p>
            {t('Stay comfy for less. Upgrade to a more energy-efficient heating system. This includes either $250 to replace an old furnace with a high-efficiency furnace equipped with an electrically commutated motor. Checkout the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}how-greenon-installations-works`} text={t('GreenON Installations Program')} />
            {locale === 'fr' && '.'}
            &nbsp;
            {locale !== 'fr' && t('by the Green Ontario Fund.')}
          </p>
          <p>
            {t('If your home is heated with electricity, you can receive up to $5,800 for an air source heat pump and $50 for a smart thermostat. To learn more about this program, check out the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text={t('Green Ontario Fund Website')} />.
          </p>
          <p>
            {t('If you are a low-income natural gas customer, you can improve the comfort and energy efficiency of your home and reduce your gas bill. Apply for a free assessment of your home to assess what energy-saving updates you can receive, and have them installed for free. To learn more about this program, check out the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text={t('Green Ontario Fund Website')} />.
          </p>
        </div>
      }
    </div>
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('cost of electricity')}
          <InlineInput
            apiKey="input_takeaction_assumption_kwhprice"
            maxLength={3}
          />
          {t('cents per kWh')}
        </li>
        <li>
          {t('Price of natural gas')}
          <InlineInput
            maxLength={3}
            apiKey="input_takeaction_assumption_ngprice"
          />
          <span
          // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('cents/{{ngUnit}} (based on your location)', {
                ngUnit: isMetric ? `${t('m', { ns: 'units' })}<sup>3</sup>` : t('therm', { ns: 'units' }),
              }),
            }}
          />
        </li>
        <li>
          {t('Price of fuel oil')}
          <InlineInput
            maxLength={3}
            apiKey="input_takeaction_thermostat_winter_fueloilprice"
          />
          {t('cents/{{unitVolume}}', { unitVolume: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        {widgetSource !== 'ontario' && <li
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('{{resultTakeactionPurchaseHighEfficiencyHeatingIntensity}} {{fuelUnits}}{{units}} consumption intensity', {
              fuelUnits: fuelUnits(
                inputTakeactionPurchaseHighEfficiencyHeatingFueltype.ensured, isMetric, t),
              resultTakeactionPurchaseHighEfficiencyHeatingIntensity,
              units: isMetric ?
                `*1000 / (${t('HDD')}*${t('m', { ns: 'units' })}<sup>2</sup>/1000)` :
                `*1000 / (${t('HDD')}*${t('ft', { ns: 'units' })}<sup>2</sup>/1000)`,
            }),
          }}
        />}
        {widgetSource !== 'ontario' && <li
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('{{resultTakeactionPurchaseHighEfficiencyHeatingGCO2fueltype}} gCO<sub>2</sub>eq emitted per {{fuelUnits}} of fuel', {
              resultTakeactionPurchaseHighEfficiencyHeatingGCO2fueltype,
              fuelUnits: fuelUnits(
                inputTakeactionPurchaseHighEfficiencyHeatingFueltype.ensured, isMetric, t),
            }),
          }}
        />}
        {widgetSource !== 'ontario' && <li
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('{{resultTakeactionPurchaseHighEfficiencyHeatingPu}} annual {{fuelUnits}} consumed', {
              resultTakeactionPurchaseHighEfficiencyHeatingPu,
              fuelUnits: fuelUnits(
                inputTakeactionPurchaseHighEfficiencyHeatingFueltype.ensured, isMetric, t),
            }),
          }}
        />}
        <li>
          {t('The cost difference between a conventional furnace and an Energy Star furnace is')}
          <InlineInput
            currency
            maxLength={7}
            apiKey="input_takeaction_purchase_high_efficiency_heating_cost_difference"
          />
        </li>
      </ul>
    </div>
  </div>
);

EfficientHeatingEquipment.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  widgetSource: PropTypes.string.isRequired,
  inputFootprintHousingSquarefeet: types.UserInput.isRequired,
  inputTakeactionPurchaseHighEfficiencyHeatingFueltype: types.UserInput.isRequired,
  resultTakeactionPurchaseHighEfficiencyHeatingDollarstypical: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyHeatingDollarsuse: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyHeatingIntensity: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyHeatingGCO2fueltype: PropTypes.string.isRequired,
  resultTakeactionPurchaseHighEfficiencyHeatingPu: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionPurchaseHighEfficiencyHeatingDollarstypical',
  'resultTakeactionPurchaseHighEfficiencyHeatingDollarsuse',
  'resultTakeactionPurchaseHighEfficiencyHeatingIntensity',
  'resultTakeactionPurchaseHighEfficiencyHeatingPu',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  isMetric: s.isMetric(state),
  locale: s.locale(state),
  widgetSource: s.widgetSource(state),
  resultTakeactionPurchaseHighEfficiencyHeatingGCO2fueltype: s.results('result_takeaction_purchase_high_efficiency_heating_gCO2fueltype', true)(state, ownProps),
  inputFootprintHousingSquarefeet: s.inputFootprintHousingSquarefeet(state),
  inputTakeactionPurchaseHighEfficiencyHeatingFueltype:
    s.inputTakeactionPurchaseHighEfficiencyHeatingFueltype(state),
});

const mapDispatchToProps = () => ({});

export default wrap(
  withT('purchaseHighEfficiencyHeating'),
  connect(mapStateToProps, mapDispatchToProps)(EfficientHeatingEquipment),
);
